// iOS-style sheet for editing items.tag_name_override (P1-18 Phase 2).
//
// Tapping the h2 tag title on ItemDetail opens this. Shows:
//   - the current auto-generated formula (read-only, dimmed) so the user
//     sees what the override is replacing
//   - a text input bound to the override value
//   - "Reset to formula" button (clears the override — stores null)
//   - "Save" button (disabled while the input is invalid or unchanged)
//
// On save, calls updateItemTagOverride with the expected_version from
// the current detail. On 409 we surface a plain-English nudge and leave
// the sheet open so the user can refetch and retry.

import { TAG_OVERRIDE_MAX, validateTagOverride } from "@betterpos/domain";
import { useEffect, useRef, useState } from "react";
import { readSessionToken } from "../../data/audit.ts";
import {
  type EditResult,
  type TagOverrideUpdate,
  updateItemTagOverride,
} from "../../data/catalog.ts";

type Props = {
  itemId: string;
  expectedVersion: number;
  currentOverride: string | null;
  // The computed formula shown under the input so the user sees what
  // happens if they clear the override. Freshly generated by the parent.
  formulaPreview: string;
  onSaved: (next: TagOverrideUpdate) => void;
  onCancel: () => void;
};

export function TagOverrideSheet({
  itemId,
  expectedVersion,
  currentOverride,
  formulaPreview,
  onSaved,
  onCancel,
}: Props) {
  const [value, setValue] = useState<string>(currentOverride ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = value.trim();
  const validation = validateTagOverride(value === "" ? null : value);
  const nextStored = value === "" ? null : trimmed;
  const changed = nextStored !== (currentOverride ?? null);
  const canSave = validation.ok && changed && !saving;

  const save = async (next: string | null) => {
    const token = readSessionToken();
    if (!token) {
      setError("Not signed in.");
      return;
    }
    setSaving(true);
    setError(null);
    const res: EditResult<TagOverrideUpdate> = await updateItemTagOverride(token, itemId, {
      expectedVersion,
      tagNameOverride: next,
    });
    setSaving(false);
    if (!res.ok) {
      setError(messageForError(res));
      return;
    }
    onSaved(res.value);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    void save(nextStored);
  };

  const onReset = () => {
    if (saving) return;
    void save(null);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit tag name"
      className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-end sm:place-items-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !saving) onCancel();
      }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="px-5 pt-5 pb-4 border-b border-slate-200/80">
          <h3 className="text-[17px] font-semibold text-slate-900">Tag name</h3>
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={
              TAG_OVERRIDE_MAX + 50 /* give room for leading/trailing spaces user hasn't trimmed */
            }
            className="mt-3 w-full h-11 rounded-lg bg-slate-100 px-3 text-[17px] text-slate-900 outline-none focus:ring-2 focus:ring-[#007aff]/40"
            placeholder={formulaPreview}
            aria-invalid={!validation.ok}
            disabled={saving}
          />
          <div className="mt-2 flex items-center justify-between text-[13px]">
            <span className="text-slate-500">
              {currentOverride === null ? (
                <>
                  Auto: <span className="text-slate-700">{formulaPreview}</span>
                </>
              ) : (
                <>
                  Reverts to: <span className="text-slate-700">{formulaPreview}</span>
                </>
              )}
            </span>
            <span
              className={trimmed.length > TAG_OVERRIDE_MAX ? "text-[#ff3b30]" : "text-slate-400"}
            >
              {trimmed.length}/{TAG_OVERRIDE_MAX}
            </span>
          </div>
          {error && <p className="mt-2 text-[13px] text-[#ff3b30]">{error}</p>}
        </div>

        {currentOverride !== null && (
          <button
            type="button"
            onClick={onReset}
            disabled={saving}
            className="w-full h-[52px] text-[17px] text-[#007aff] active:bg-slate-100 transition border-b border-slate-200/80 disabled:opacity-50"
          >
            Reset to formula
          </button>
        )}
        <button
          type="submit"
          disabled={!canSave}
          className="w-full h-[52px] text-[17px] font-semibold text-[#007aff] active:bg-slate-100 transition disabled:text-slate-400 disabled:active:bg-transparent"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="w-full h-[52px] text-[17px] text-slate-500 active:bg-slate-100 transition border-t border-slate-200/80 disabled:opacity-50"
        >
          Cancel
        </button>
      </form>
    </div>
  );
}

function messageForError(res: EditResult<unknown> & { ok: false }): string {
  switch (res.error) {
    case "version_conflict":
      return "Someone else just edited this item. Close and reopen to see the latest.";
    case "validation_failed":
      if (res.reason === "too_long") return `Too long (over ${TAG_OVERRIDE_MAX} characters).`;
      if (res.reason === "empty_after_trim") return "Enter at least one non-space character.";
      return "That value isn't valid.";
    case "not_found":
      return "This item no longer exists.";
    default:
      return "Couldn't save. Try again in a moment.";
  }
}
